import { Component, OnDestroy, OnInit } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Subscription } from 'rxjs';
import { ThemeCustomizerService } from '../../common/theme-customizer/theme-customizer.service';

interface SelectOption {
  id: number;
  label: string;
}

interface InquiryResponse {
  success?: boolean;
  message?: string;
  data?: {
    id?: number;
    institution_id?: number;
    student_name?: string;
    phone?: string;
  };
  meta?: {
    error?: string | null;
    where?: string;
  };
}

interface InquiryMetaResponse {
  success?: boolean;
  message?: string;
  data?: {
    academic_years?: SelectOption[];
    courses?: Array<{
      id: number;
      label: string;
      level?: string;
    }>;
  };
  meta?: {
    error?: string | null;
    where?: string;
  };
}

interface InquiryFormModel {
  student_name: string;
  gender: string;
  phone: string;
  email: string;
  parent_name: string;
  address: string;
  preferred_course_id: number | null;
  academic_year_id: number | null;
  preferred_time: string;
  remarks: string;
  consent: boolean;
}

type InquiryFieldKey = keyof InquiryFormModel;
type TextFieldKey =
  | 'student_name'
  | 'gender'
  | 'phone'
  | 'email'
  | 'parent_name'
  | 'address'
  | 'preferred_time'
  | 'remarks';

@Component({
  selector: 'app-blog-details-page',
  templateUrl: './blog-details-page.component.html',
  styleUrls: ['./blog-details-page.component.scss']
})
export class BlogDetailsPageComponent implements OnInit, OnDestroy {
  isToggled = false;
  private themeSub?: Subscription;

  apiUrl = 'https://stmichaelgroup.in/ERP/backend/admission/addWebsiteInquiry.php';
  metaUrl = 'https://stmichaelgroup.in/ERP/backend/admission/getWebsiteInquiryMeta.php';

  academicYears: SelectOption[] = [];
  courseOptions: SelectOption[] = [];

  loadingMeta = false;
  metaLoadError = '';

  genderOptions: string[] = ['Male', 'Female', 'Other'];
  preferredTimeOptions: string[] = ['Morning', 'Afternoon', 'Evening'];

  form: InquiryFormModel = this.getInitialForm();

  submitting = false;
  submitSuccess = '';
  submitError = '';

  showSuccessPopup = false;
  popupTitle = 'Thank You!';
  popupMessage =
    'Thank you for your enquiry. Our admission team will contact you soon with programme details and next-step guidance.';
  popupEnquiryId: number | null = null;

  activeHelp: InquiryFieldKey | '' = '';
  attemptedSubmit = false;
  touched: Partial<Record<InquiryFieldKey, boolean>> = {};

  helperTexts: Record<InquiryFieldKey, string> = {
    student_name: 'Enter the student name as per school or official academic records.',
    gender: 'Select the student gender for basic enquiry profiling.',
    phone: 'Enter a valid 10-digit mobile number. WhatsApp number is preferred.',
    email: 'Admission updates and follow-up details can be shared to this email.',
    parent_name: 'Enter parent or guardian name for counselling follow-up if needed.',
    address: 'Enter your current city/address so the admission team can understand your location.',
    preferred_course_id: 'Choose the programme you are interested in joining.',
    academic_year_id: 'Select the academic year in which you plan to seek admission.',
    preferred_time: 'Choose a comfortable time slot for our team to contact you.',
    remarks: 'You may ask about fees, hostel, transport, eligibility, counselling, or scholarships.',
    consent: 'Consent is required so the admission office can contact you regarding your enquiry.'
  };

  constructor(
    private http: HttpClient,
    public themeService: ThemeCustomizerService
  ) {
    this.themeSub = this.themeService.isToggled$.subscribe((isToggled) => {
      this.isToggled = isToggled;
    });
  }

  ngOnInit(): void {
    this.loadMeta();
  }

  ngOnDestroy(): void {
    this.themeSub?.unsubscribe();
  }

  getInitialForm(): InquiryFormModel {
    return {
      student_name: '',
      gender: '',
      phone: '',
      email: '',
      parent_name: '',
      address: '',
      preferred_course_id: null,
      academic_year_id: null,
      preferred_time: '',
      remarks: '',
      consent: false
    };
  }

  loadMeta(): void {
    this.loadingMeta = true;
    this.metaLoadError = '';

    this.http.get<InquiryMetaResponse>(this.metaUrl).subscribe({
      next: (response) => {
        this.loadingMeta = false;

        if (!response?.success) {
          this.metaLoadError =
            response?.message || 'Unable to load enquiry form options.';
          return;
        }

        this.academicYears = response?.data?.academic_years || [];
        this.courseOptions = (response?.data?.courses || []).map((item) => ({
          id: item.id,
          label: item.level ? `${item.label} (${item.level})` : item.label
        }));

        if (!this.form.academic_year_id && this.academicYears.length > 0) {
          this.form.academic_year_id = this.academicYears[0].id;
        }
      },
      error: (error) => {
        this.loadingMeta = false;
        this.metaLoadError =
          error?.error?.message ||
          error?.error?.debug ||
          error?.error?.meta?.error ||
          error?.message ||
          'Unable to load academic year and course list.';
      }
    });
  }

  onFieldFocus(field: InquiryFieldKey): void {
    this.activeHelp = field;
  }

  onFieldBlur(field: InquiryFieldKey): void {
    this.touched[field] = true;
    if (this.activeHelp === field) {
      this.activeHelp = '';
    }
  }

  onFieldInput(field: TextFieldKey, event: Event): void {
    const target = event.target as
      | HTMLInputElement
      | HTMLTextAreaElement
      | HTMLSelectElement;

    this.form[field] = target.value as never;
  }

  onNumberFieldInput(
    field: 'preferred_course_id' | 'academic_year_id',
    event: Event
  ): void {
    const target = event.target as HTMLSelectElement;
    const rawValue = target.value;
    this.form[field] = rawValue ? Number(rawValue) : null;
  }

  onConsentChange(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.form.consent = !!target.checked;
  }

  shouldShowHelper(field: InquiryFieldKey): boolean {
    return this.activeHelp === field && !this.shouldShowError(field);
  }

  shouldShowError(field: InquiryFieldKey): boolean {
    return (
      !!this.getFieldError(field) &&
      (!!this.touched[field] || this.attemptedSubmit)
    );
  }

  getFieldError(field: InquiryFieldKey): string | null {
    switch (field) {
      case 'student_name':
        if (this.form.student_name.trim() === '') {
          return 'Student name is required.';
        }
        if (this.form.student_name.trim().length < 3) {
          return 'Student name must contain at least 3 characters.';
        }
        return null;

      case 'gender':
        return null;

      case 'phone':
        if (this.form.phone.trim() === '') {
          return 'Mobile number is required.';
        }
        if (!/^[6-9][0-9]{9}$/.test(this.form.phone.trim())) {
          return 'Enter a valid 10-digit mobile number starting with 6, 7, 8, or 9.';
        }
        return null;

      case 'email':
        if (!this.form.email.trim()) {
          return null;
        }
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.form.email.trim())) {
          return 'Enter a valid email address.';
        }
        return null;

      case 'parent_name':
        return null;

      case 'address':
        return null;

      case 'preferred_course_id':
        if (!this.form.preferred_course_id) {
          return 'Please select a preferred course.';
        }
        return null;

      case 'academic_year_id':
        if (!this.form.academic_year_id) {
          return 'Please select an academic year.';
        }
        return null;

      case 'preferred_time':
        return null;

      case 'remarks':
        return null;

      case 'consent':
        if (!this.form.consent) {
          return 'You must provide consent before submitting the enquiry.';
        }
        return null;

      default:
        return null;
    }
  }

  private getFirstValidationError(): string | null {
    const orderedFields: InquiryFieldKey[] = [
      'student_name',
      'phone',
      'email',
      'academic_year_id',
      'preferred_course_id',
      'consent'
    ];

    for (const field of orderedFields) {
      const error = this.getFieldError(field);
      if (error) {
        return error;
      }
    }

    return null;
  }

  resetForm(): void {
    const selectedAcademicYear = this.form.academic_year_id;

    this.form = this.getInitialForm();
    this.submitSuccess = '';
    this.submitError = '';
    this.activeHelp = '';
    this.attemptedSubmit = false;
    this.touched = {};

    if (selectedAcademicYear) {
      this.form.academic_year_id = selectedAcademicYear;
    }
  }

  openSuccessPopup(enquiryId?: number): void {
    this.popupEnquiryId = enquiryId ?? null;
    this.showSuccessPopup = true;
  }

  closeSuccessPopup(): void {
    this.showSuccessPopup = false;
    this.popupEnquiryId = null;
  }

  submitInquiry(event: Event): void {
    event.preventDefault();

    this.submitError = '';
    this.submitSuccess = '';
    this.attemptedSubmit = true;

    const validationError = this.getFirstValidationError();
    if (validationError) {
      this.submitError = validationError;
      return;
    }

    const payload = {
      student_name: this.form.student_name.trim(),
      gender: this.form.gender || null,
      phone: this.form.phone.trim(),
      email: this.form.email.trim() || null,
      parent_name: this.form.parent_name.trim() || null,
      address: this.form.address.trim() || null,
      preferred_course_id: this.form.preferred_course_id,
      academic_year_id: this.form.academic_year_id,
      source: 'Website',
      campaign: 'Angular Website Inquiry',
      preferred_time: this.form.preferred_time || null,
      remarks: this.form.remarks.trim() || null,
      stage: 'inquiry',
      status: 'new',
      lead_status: 'new',
      lead_priority: 'medium',
      consent: this.form.consent ? 1 : 0,
      current_page:
        typeof window !== 'undefined' ? window.location.pathname : '/inquiry'
    };

    this.submitting = true;

    this.http.post<InquiryResponse>(this.apiUrl, payload).subscribe({
      next: (response) => {
        this.submitting = false;

        if (response?.success) {
          const enquiryId = response?.data?.id ?? null;

          this.resetForm();
          this.submitSuccess = '';
          this.submitError = '';
          this.openSuccessPopup(enquiryId ?? undefined);

          if (typeof window !== 'undefined') {
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }
          return;
        }

        this.submitError = response?.message || 'Unable to submit enquiry.';
      },
      error: (error) => {
        this.submitting = false;

        const serverMessage =
          error?.error?.message ||
          error?.error?.debug ||
          error?.error?.meta?.error ||
          error?.message ||
          'Server error while saving enquiry.';

        if (serverMessage === 'DUPLICATE_INQUIRY_EXISTS') {
          this.submitError =
            'An enquiry with this mobile number or email already exists.';
          return;
        }

        if (serverMessage === 'INVALID_ACADEMIC_YEAR') {
          this.submitError =
            'Selected academic year is invalid. Please reload the page and try again.';
          return;
        }

        if (serverMessage === 'INVALID_COURSE_SELECTION') {
          this.submitError =
            'Selected course is invalid. Please reload the page and try again.';
          return;
        }

        if (serverMessage === 'CONSENT_REQUIRED') {
          this.submitError =
            'Please accept the consent checkbox before submitting.';
          return;
        }

        if (serverMessage === 'DB connection failed') {
          this.submitError =
            'Database connection failed in backend. Please check cPanel DB user, password, and DB privileges.';
          return;
        }

        this.submitError = serverMessage;
      }
    });
  }
}